/**
 * Listen and Respond Exercise
 * User listens to a passage and answers sequential multiple-choice questions.
 * Transcript toggle available after all questions are answered.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, Text, VStack, HStack } from '@gluestack-ui/themed';
import { colors, FS_XS, FS_SM } from '../../theme';
import { useKeyboardShortcut, useNumberKeyShortcuts } from '../../hooks/useKeyboardShortcut';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface Question {
  question: string;
  options: string[];
  correctAnswer: string;
  explanation?: string;
}

interface ListenAndRespondExerciseProps {
  prompt: string;
  passage: string;
  passageTitle?: string;
  questions: Question[];
  showTranscript?: boolean;
  speed?: string;
  hint?: string;
  onComplete: (correct: boolean, score: number) => void;
}

export const ListenAndRespondExercise = React.memo(function ListenAndRespondExercise({
  prompt,
  passage,
  passageTitle,
  questions,
  showTranscript: _initialShowTranscript,
  speed,
  hint,
  onComplete,
}: ListenAndRespondExerciseProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();

  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [submittedQuestions, setSubmittedQuestions] = useState<Set<number>>(new Set());
  const [showTranscript, setShowTranscript] = useState(false);
  const [allDone, setAllDone] = useState(false);

  const pointsPerQuestion = useMemo(
    () => (questions.length > 0 ? Math.round(100 / questions.length) : 0),
    [questions.length]
  );

  const handleOptionSelect = useCallback(
    (questionIdx: number, option: string) => {
      if (submittedQuestions.has(questionIdx)) return;
      setAnswers((prev) => ({ ...prev, [questionIdx]: option }));
    },
    [submittedQuestions]
  );

  const handleQuestionSubmit = useCallback(
    (questionIdx: number) => {
      if (submittedQuestions.has(questionIdx) || !answers[questionIdx]) return;

      const newSubmitted = new Set(submittedQuestions);
      newSubmitted.add(questionIdx);
      setSubmittedQuestions(newSubmitted);

      // If this is the last question, calculate total score
      if (newSubmitted.size === questions.length) {
        let totalScore = 0;
        for (let i = 0; i < questions.length; i++) {
          if (answers[i] === questions[i].correctAnswer) {
            totalScore += pointsPerQuestion;
          }
        }
        // Ensure max is 100 (rounding fix for last question)
        if (totalScore > 0 && totalScore < 100) {
          let correctCount = 0;
          for (let i = 0; i < questions.length; i++) {
            if (answers[i] === questions[i].correctAnswer) correctCount++;
          }
          if (correctCount === questions.length) totalScore = 100;
        }
        setAllDone(true);
        onComplete(totalScore >= 80, totalScore);
      } else {
        // Advance to next question after a brief moment
        setCurrentQuestionIdx(questionIdx + 1);
      }
    },
    [submittedQuestions, answers, questions, pointsPerQuestion, onComplete]
  );

  // Current question for keyboard shortcuts
  const currentQuestion = questions[currentQuestionIdx];
  const currentQuestionOptions = useMemo(
    () => currentQuestion?.options ?? [],
    [currentQuestion?.options]
  );
  const isCurrentSubmitted = submittedQuestions.has(currentQuestionIdx);

  const handleCurrentOptionByIndex = useCallback((i: number) => {
    if (i < currentQuestionOptions.length) {
      handleOptionSelect(currentQuestionIdx, currentQuestionOptions[i]);
    }
  }, [currentQuestionIdx, currentQuestionOptions, handleOptionSelect]);

  const handleCurrentQuestionSubmit = useCallback(() => {
    handleQuestionSubmit(currentQuestionIdx);
  }, [currentQuestionIdx, handleQuestionSubmit]);

  // Keyboard shortcuts: number keys select options for current question, Enter submits (web only, no-op on native)
  useNumberKeyShortcuts(currentQuestionOptions.length, handleCurrentOptionByIndex, !isCurrentSubmitted && !allDone);
  useKeyboardShortcut('Enter', handleCurrentQuestionSubmit, { enabled: !isCurrentSubmitted && !allDone && !!answers[currentQuestionIdx] });

  const totalScore = useMemo(() => {
    if (!allDone) return 0;
    let score = 0;
    for (let i = 0; i < questions.length; i++) {
      if (answers[i] === questions[i].correctAnswer) {
        score += pointsPerQuestion;
      }
    }
    // Fix rounding for perfect score
    let correctCount = 0;
    for (let i = 0; i < questions.length; i++) {
      if (answers[i] === questions[i].correctAnswer) correctCount++;
    }
    if (correctCount === questions.length) return 100;
    return score;
  }, [allDone, questions, answers, pointsPerQuestion]);

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
    >
      <VStack space="md">
        {/* Prompt */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
        >
          {prompt}
        </Text>

        {/* Audio play area */}
        <Box
          p="$4"
          rounded="$lg"
          alignItems="center"
          style={{ backgroundColor: theme.primarySoft }}
        >
          <HStack space="md" alignItems="center">
            <Text allowFontScaling={false} style={{ fontSize: 32 }}>🔊</Text>
            <VStack>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.primary, ...ffs }}
              >
                {passageTitle || 'Listen to the passage'}
              </Text>
              {speed && (
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: theme.primary, ...ffs }}
                >
                  Speed: {speed}
                </Text>
              )}
            </VStack>
          </HStack>
        </Box>

        {hint && !allDone && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Progress indicator */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
        >
          Question {Math.min(currentQuestionIdx + 1, questions.length)} of {questions.length}
        </Text>

        {/* Questions (show current and all previously answered) */}
        <VStack space="md">
          {questions.map((q, qIdx) => {
            if (qIdx > currentQuestionIdx) return null;

            const isSubmitted = submittedQuestions.has(qIdx);
            const selectedAnswer = answers[qIdx];
            const isQuestionCorrect = selectedAnswer === q.correctAnswer;

            return (
              <VStack key={qIdx} space="sm">
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_SM * fontScale, fontWeight: '500', color: theme.text, ...ffs }}
                >
                  {qIdx + 1}. {q.question}
                </Text>

                <VStack space="xs">
                  {q.options.map((option, oIdx) => {
                    const isSelected = selectedAnswer === option;
                    const isOptionCorrect = option === q.correctAnswer;

                    let bgColor: string = theme.isDark ? '#374151' : '#F3F4F6';
                    let borderColor: string = theme.border;

                    if (!isSubmitted && isSelected) {
                      bgColor = colors.primary;
                      borderColor = colors.primary;
                    } else if (isSubmitted && isSelected) {
                      bgColor = isQuestionCorrect ? '#D1FAE5' : '#FEE2E2';
                      borderColor = isQuestionCorrect ? '#059669' : '#DC2626';
                    } else if (isSubmitted && isOptionCorrect) {
                      bgColor = '#D1FAE5';
                      borderColor = '#059669';
                    }

                    return (
                      <TouchableOpacity
                        key={oIdx}
                        testID={`respond-q${qIdx}-option-${oIdx}`}
                        accessibilityLabel={option}
                        accessibilityRole="button"
                        accessibilityState={{ selected: isSelected }}
                        onPress={() => handleOptionSelect(qIdx, option)}
                        disabled={isSubmitted}
                        activeOpacity={0.7}
                      >
                        <Box
                          px="$4"
                          py="$3"
                          rounded="$md"
                          style={{ backgroundColor: bgColor, borderWidth: 1, borderColor }}
                        >
                          <Text
                            allowFontScaling={false}
                            style={{
                              fontSize: FS_SM * fontScale,
                              fontWeight: '500',
                              color: !isSubmitted && isSelected ? '#FFFFFF' : theme.text,
                              ...ffs,
                            }}
                          >
                            {option}
                          </Text>
                        </Box>
                      </TouchableOpacity>
                    );
                  })}
                </VStack>

                {/* Per-question submit */}
                {!isSubmitted && selectedAnswer && (
                  <TouchableOpacity
                    testID={`respond-q${qIdx}-submit`}
                    accessibilityLabel={`Submit answer for question ${qIdx + 1}`}
                    accessibilityRole="button"
                    onPress={() => handleQuestionSubmit(qIdx)}
                    activeOpacity={0.7}
                  >
                    <Box
                      py="$2"
                      rounded="$md"
                      alignItems="center"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <Text
                        allowFontScaling={false}
                        style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#FFFFFF', ...ffs }}
                      >
                        Check
                      </Text>
                    </Box>
                  </TouchableOpacity>
                )}

                {/* Per-question feedback */}
                {isSubmitted && (
                  <Box
                    p="$2"
                    rounded="$md"
                    style={{ backgroundColor: isQuestionCorrect ? '#D1FAE5' : '#FEE2E2' }}
                  >
                    <Text
                      allowFontScaling={false}
                      style={{
                        fontSize: FS_SM * fontScale,
                        fontWeight: '600',
                        color: isQuestionCorrect ? '#065F46' : '#991B1B',
                        ...ffs,
                      }}
                    >
                      {isQuestionCorrect ? '✓ Correct!' : `✗ Answer: ${q.correctAnswer}`}
                    </Text>
                    {q.explanation && !isQuestionCorrect && (
                      <Text
                        allowFontScaling={false}
                        style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
                      >
                        {q.explanation}
                      </Text>
                    )}
                  </Box>
                )}
              </VStack>
            );
          })}
        </VStack>

        {/* Final score */}
        {allDone && (
          <Box
            p="$2"
            rounded="$md"
            style={{ backgroundColor: totalScore >= 80 ? '#D1FAE5' : '#FEE2E2' }}
          >
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_SM * fontScale,
                fontWeight: '600',
                color: totalScore >= 80 ? '#065F46' : '#991B1B',
                ...ffs,
              }}
            >
              {totalScore >= 80 ? `✓ Total score: ${totalScore}%` : `✗ Total score: ${totalScore}%`}
            </Text>
          </Box>
        )}

        {/* Transcript toggle (only after all done) */}
        {allDone && (
          <VStack space="xs">
            <TouchableOpacity
              testID="respond-transcript-toggle"
              accessibilityLabel={showTranscript ? 'Hide transcript' : 'Show transcript'}
              accessibilityRole="button"
              onPress={() => setShowTranscript(!showTranscript)}
              activeOpacity={0.7}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_XS * fontScale, color: theme.primary, fontWeight: '500', ...ffs }}
              >
                {showTranscript ? '▾ Hide transcript' : '▸ Show transcript'}
              </Text>
            </TouchableOpacity>
            {showTranscript && (
              <Box
                p="$3"
                rounded="$md"
                style={{ backgroundColor: theme.isDark ? '#1E3A5F' : '#EFF6FF', borderWidth: 1, borderColor: theme.primary }}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_SM * fontScale, color: theme.text, fontStyle: 'italic', ...ffs }}
                >
                  {passage}
                </Text>
              </Box>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
});
